/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
    webLightTheme,
    webDarkTheme,
    Theme,
    createLightTheme,
    createDarkTheme,
    BrandVariants
} from '@fluentui/react-components';

export const createBrandVariantsFromPalette = (basePalette: any): BrandVariants => {
    return {
        10: basePalette?.BkgAccentTint || '#061724',
        20: basePalette?.BkgAccentTint || '#082338',
        30: basePalette?.BkgAccentTint || '#0a2e4a',
        40: basePalette?.BkgAccentTint || '#0c3b5e',
        50: basePalette?.BkgAccentTint || '#0e4775',
        60: basePalette?.BkgCtlEmphasis || '#0f548c',
        70: basePalette?.BkgCtlEmphasis || '#115ea3',
        80: basePalette?.BkgCtlEmphasis || '#0f6cbd',
        90: basePalette?.BkgCtlEmphasisHover || '#2886de',
        100: basePalette?.BkgCtlEmphasisHover || '#479ef5',
        110: basePalette?.BkgCtlEmphasisHover || '#62abf5',
        120: basePalette?.BkgCtlEmphasisHover || '#77b7f7',
        130: basePalette?.TextEmphasis || '#96c6fa',
        140: basePalette?.TextEmphasis || '#b4d6fa',
        150: basePalette?.TextEmphasisHover || '#cfe4fa',
        160: basePalette?.TextEmphasisHover || '#ebf3fc',
    };
};

export const createThemeFromFluentData = (fluentThemeData: any, isDark: boolean): Theme => {
    const basePalette = fluentThemeData?.Palettes?.Base;
    const cardPalette = fluentThemeData?.Palettes?.Card;

    if (!basePalette) {
        return isDark ? webDarkTheme : webLightTheme;
    }

    const brandVariants = createBrandVariantsFromPalette(basePalette);
    const customTheme = isDark ? createDarkTheme(brandVariants) : createLightTheme(brandVariants);

    // Override with Office palette colors
    return {
        ...customTheme,
        colorNeutralBackground1: cardPalette?.Bkg || basePalette.BkgHeader,
        colorNeutralBackground2: basePalette.BkgSubtle,
        colorNeutralBackground3: basePalette.Bkg,
        colorBrandBackground: basePalette.BkgCtlEmphasis,
        colorBrandBackgroundHover: basePalette.BkgCtlEmphasisHover,
        colorBrandBackgroundPressed: basePalette.BkgCtlEmphasisPressed,
        colorNeutralForeground1: cardPalette?.Text || basePalette.Text,
        colorNeutralForeground2: basePalette.TextSecondary,
        colorNeutralForeground3: cardPalette?.TextSecondary || basePalette.TextSecondary,
        colorBrandForeground1: basePalette.TextEmphasis,
        colorBrandForeground2: basePalette.TextEmphasis2,
    };
};

export const getBackgroundColorFromThemeData = (fluentThemeData: any): string | undefined => {
    return fluentThemeData?.Palettes?.Base?.Bkg;
};
